import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 15 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: "rgba(255,255,255,0.6)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#fff" },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")<{
  active?: boolean;
  completed?: boolean;
  activecolor?: string;
}>(({ active, completed, activecolor }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: completed
    ? "#fff"
    : active
      ? "#fff"
      : "rgba(255,255,255,0.2)",
  color:
    completed || active ? (activecolor ?? "#724E41") : "rgba(255,255,255,0.6)",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: active ? "0 0 0 3px rgba(255,255,255,0.4)" : "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

export interface FormWizardProps {
  steps: string[];
  activeStep: number;
  onStepClick?: (step: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  onFinish: () => void;
  isSubmitting?: boolean;
  finishLabel?: string;
  nextLabel?: string;
  previousLabel?: string;
  headerAction?: { label: string; onClick: () => void };
  previewSlot?: React.ReactNode;
  children: React.ReactNode;
}

export default function FormWizard({
  steps,
  activeStep,
  onStepClick,
  onNext,
  onPrevious,
  onFinish,
  isSubmitting = false,
  finishLabel = "Save",
  nextLabel = "Next",
  previousLabel = "Previous",
  headerAction,
  previewSlot,
  children,
}: FormWizardProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color10 = reduxTheme.primary_colors?.[10];
  const color400 = reduxTheme.primary_colors?.[400];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const isLastStep = activeStep === steps.length - 1;

  const mainColumn = (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: 4,
        maxHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        backgroundColor: color900,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 3,
          pb: 2,
          pt: 5,
          position: "relative",
          overflow: "hidden",
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${color400} 0%, ${color10} 50%, ${color400} 100%)`,
        }}
      >
        {headerAction && (
          <Box sx={{ position: "absolute", top: 12, left: 24 }}>
            <Button
              onClick={headerAction.onClick}
              disabled={isSubmitting}
              sx={{
                color: "#fff",
                textTransform: "none",
                textDecoration: "underline",
                fontSize: "13px",
                fontWeight: 800,
                minWidth: "auto",
                p: 0,
                "&:hover": { backgroundColor: "transparent", color: "#fff" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              {headerAction.label}
            </Button>
          </Box>
        )}

        <Box sx={{ position: "relative" }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<CustomConnector />}
          >
            {steps.map((label, index) => (
              <Step
                key={label}
                onClick={() => onStepClick?.(index)}
                sx={{ cursor: onStepClick ? "pointer" : "default" }}
              >
                <StepLabel
                  StepIconComponent={() => (
                    <CustomStepIcon
                      active={index === activeStep}
                      completed={index < activeStep}
                      activecolor={color400}
                    >
                      {index + 1}
                    </CustomStepIcon>
                  )}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        index <= activeStep ? "#fff" : "rgba(255,255,255,0.5)",
                      fontWeight: index === activeStep ? 700 : 400,
                      letterSpacing: "0.02em",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>

      {/* Step content */}
      <Box
        sx={{
          p: 4,
          flex: 1,
          overflowY: "auto",
          backgroundColor: color900,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: color800,
            borderRadius: "10px",
          },
        }}
      >
        {children}
      </Box>

      {/* Footer navigation */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: `1px solid ${color800}`,
          backgroundColor: color900,
        }}
      >
        <Button
          onClick={onPrevious}
          disabled={activeStep === 0 || isSubmitting}
          sx={{
            color: "#000",
            textTransform: "none",
            textDecoration: "underline",
            fontSize: "14px",
            minWidth: "auto",
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            "&.Mui-disabled": { opacity: 0.3 },
          }}
        >
          {previousLabel}
        </Button>

        <Button
          variant="contained"
          onClick={isLastStep ? onFinish : onNext}
          disabled={isSubmitting}
          startIcon={
            isSubmitting && isLastStep ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          sx={{
            color: "#fff",
            backgroundColor: color400,
            px: 6,
            py: 1.5,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "14px",
            minWidth: "90px",
            height: "40px",
            "&:hover": { backgroundColor: color10 },
          }}
        >
          {isSubmitting && isLastStep
            ? "Saving..."
            : isLastStep
              ? finishLabel
              : nextLabel}
        </Button>
      </Box>
    </Paper>
  );

  if (!previewSlot) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: color800, p: 3 }}>
        {mainColumn}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: color800, p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }} sx={{ display: "flex" }}>
          {mainColumn}
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box
            sx={{
              position: "sticky",
              top: 24,
              height: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {previewSlot}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
